import { FC, useEffect, useMemo, useState } from 'react'
import {
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ShipmentPieceRow } from '../../interfaces/Import/ShipmentGroupInterface'
import { FormSelect, FormText, PrimaryButton } from '..'

type OptionLike = { id?: any; name?: string; value?: any; description?: string }

interface PieceModalState {
  open: boolean
  pieceIndex: number | null
  draft: Partial<ShipmentPieceRow>
  draftErrors: Partial<Record<keyof ShipmentPieceRow, string>>
}

const validatePieceModal = (
  p: Partial<ShipmentPieceRow>
): Partial<Record<keyof ShipmentPieceRow, string>> => {
  const e: Partial<Record<keyof ShipmentPieceRow, string>> = {}
  if (!p.quantity || p.quantity < 1) e.quantity = 'Debe ser ≥ 1'
  if (!p.weight || p.weight <= 0) e.weight = 'Debe ser > 0'
  if (!p.height || p.height <= 0) e.height = 'Debe ser > 0'
  if (!p.width || p.width <= 0) e.width = 'Debe ser > 0'
  if (!p.length || p.length <= 0) e.length = 'Debe ser > 0'
  if (!p.declaredValue || p.declaredValue <= 0) e.declaredValue = 'Debe ser > 0'
  if (!p.categoryId) e.categoryId = 'Requerido'
  return e
}

export const ShipmentBulkUploadPiecesList: FC<{
  groupIndex: number
  pieces: ShipmentPieceRow[]
  pieceCategories: OptionLike[]
  onChangePiece: (pieceIndex: number, patch: Partial<ShipmentPieceRow>) => void
  onAddPiece: () => void
  onRemovePiece: (pieceIndex: number) => void
  validatePiece: (p: ShipmentPieceRow) => Partial<Record<keyof ShipmentPieceRow, string>>
}> = ({
  groupIndex,
  pieces,
  pieceCategories,
  onChangePiece,
  onAddPiece,
  onRemovePiece,
  validatePiece,
}) => {
  const [modal, setModal] = useState<PieceModalState>({
    open: false,
    pieceIndex: null,
    draft: {},
    draftErrors: {},
  })
  const [addingNew, setAddingNew] = useState(false)

  // Close modal on Escape
  useEffect(() => {
    if (!modal.open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [modal.open])

  const openAdd = () => {
    onAddPiece()
    setAddingNew(true)
    setModal({
      open: true,
      pieceIndex: null,
      draft: { quantity: 1, categoryId: '', weight: 0, height: 0, width: 0, length: 0, declaredValue: 0 },
      draftErrors: {},
    })
  }

  const openEdit = (idx: number) => {
    setAddingNew(false)
    setModal({
      open: true,
      pieceIndex: idx,
      draft: { ...pieces[idx] },
      draftErrors: {},
    })
  }

  const closeModal = () => {
    if (addingNew) {
      onRemovePiece(pieces.length - 1)
    }
    setAddingNew(false)
    setModal({ open: false, pieceIndex: null, draft: {}, draftErrors: {} })
  }

  const updateDraft = (patch: Partial<ShipmentPieceRow>) => {
    setModal((prev) => {
      const newDraft = { ...prev.draft, ...patch }
      return { ...prev, draft: newDraft, draftErrors: validatePieceModal(newDraft) }
    })
  }

  const handleSave = () => {
    const errs = validatePieceModal(modal.draft)
    if (Object.keys(errs).length > 0) {
      setModal((prev) => ({ ...prev, draftErrors: errs }))
      return
    }
    const idx = modal.pieceIndex !== null ? modal.pieceIndex : pieces.length - 1
    onChangePiece(idx, modal.draft as ShipmentPieceRow)
    setAddingNew(false)
    setModal({ open: false, pieceIndex: null, draft: {}, draftErrors: {} })
  }

  const catOption = (categoryId: string) =>
    pieceCategories.find((c) => String(c.id ?? '') === String(categoryId ?? ''))

  const isGuardarDisabled = useMemo(
    () => Object.keys(validatePieceModal(modal.draft)).length > 0,
    [modal.draft]
  )

  return (
    <div className="space-y-3">
      {pieces.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <ArchiveBoxIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">Sin piezas registradas.</p>
          <p className="text-xs text-gray-400 mb-3">Añade al menos una pieza para poder enviar.</p>
          <PrimaryButton onClick={openAdd} className="text-sm mx-auto">
            <PlusIcon className="w-4 h-4 mr-1" />
            Añadir pieza
          </PrimaryButton>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pieces.map((piece, pIdx) => {
              const pieceErrors = validatePiece(piece)
              const hasPieceError = Object.keys(pieceErrors).length > 0
              const catName = catOption(piece.categoryId)?.name || piece.categoryId || '—'
              const errorTip = hasPieceError ? Object.values(pieceErrors).join(' | ') : undefined

              return (
                <div
                  key={pIdx}
                  className={`relative border rounded-lg p-3 pr-2 bg-white ${
                    hasPieceError ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`}
                  title={errorTip}
                >
                  {/* FINDING-009 (design-review round 2): edit/delete icons moved
                      to top-right corner (was bottom-right). Convention for card
                      actions = top-right; instant affordance, less scroll on
                      cramped mobile cards. */}
                  <div className="absolute top-2 right-2 flex gap-0.5 z-10">
                    <button
                      type="button"
                      onClick={() => openEdit(pIdx)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-white/80 rounded-md transition-colors"
                      aria-label={`Editar pieza ${pIdx + 1}`}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemovePiece(pIdx)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-white/80 rounded-md transition-colors"
                      aria-label={`Eliminar pieza ${pIdx + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {hasPieceError && (
                    <ExclamationTriangleIcon className="absolute top-2 left-2 w-4 h-4 text-red-500" />
                  )}

                  <div className={`flex items-start justify-between gap-2 mb-1 ${hasPieceError ? 'pl-6' : ''} pr-20`}>
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {piece.productName || catName}
                    </span>
                    <span className="text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      {piece.quantity || 1} pza(s)
                    </span>
                  </div>

                  {piece.productCode && (
                    <p className={`text-xs text-gray-400 mb-1 ${hasPieceError ? 'pl-6' : ''}`}>Código: {piece.productCode}</p>
                  )}

                  <div className={`text-xs text-gray-500 space-y-0.5 ${hasPieceError ? 'pl-6' : ''}`}>
                    <p>Peso Balance: {piece.weight} kg &nbsp;|&nbsp; {piece.height}×{piece.width}×{piece.length} cm</p>
                    <p>Valor: ${(piece.declaredValue || 0).toFixed(2)}</p>
                  </div>

                  {piece.mergedRowCount && piece.mergedRowCount > 1 && (
                    <span className={`inline-block mt-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded ${hasPieceError ? 'ml-6' : ''}`}>
                      Fusionadas: {piece.mergedRowCount} filas
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          <PrimaryButton onClick={openAdd} className="text-sm flex items-center gap-1">
            <PlusIcon className="w-4 h-4" />
            Añadir pieza
          </PrimaryButton>
        </>
      )}

      {/* Modal editor de pieza */}
      {modal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`piece-modal-title-${groupIndex}`}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2
                id={`piece-modal-title-${groupIndex}`}
                className="font-semibold text-gray-800 text-base"
              >
                {modal.pieceIndex !== null ? 'Editar pieza' : 'Nueva pieza'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Cerrar modal"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <FormSelect<OptionLike>
                id={`modal-category-${groupIndex}`}
                name={`modal-category-${groupIndex}`}
                label="Producto *"
                selected={catOption(modal.draft.categoryId || '') ?? undefined}
                options={pieceCategories}
                optionString={(opt) => opt.name || String(opt.id || '')}
                onSelectOption={(opt) => updateDraft({ categoryId: String(opt.id ?? '') })}
                error={modal.draftErrors.categoryId}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormText
                  id={`modal-quantity-${groupIndex}`}
                  name={`modal-quantity-${groupIndex}`}
                  label="Cantidad (Nº piezas) *"
                  value={modal.draft.quantity !== undefined && modal.draft.quantity !== 0 ? String(modal.draft.quantity) : ''}
                  inputMode="numeric"
                  onChange={(e: any) => {
                    const v = parseInt(e.target.value)
                    updateDraft({ quantity: isNaN(v) ? 0 : Math.max(0, v) })
                  }}
                  error={modal.draftErrors.quantity}
                />
                <FormText
                  id={`modal-weight-${groupIndex}`}
                  name={`modal-weight-${groupIndex}`}
                  label="Peso Balance (kg) *"
                  value={modal.draft.weight ? String(modal.draft.weight) : ''}
                  inputMode="decimal"
                  onChange={(e: any) => {
                    const v = parseFloat(e.target.value.replace(',', '.'))
                    updateDraft({ weight: isNaN(v) ? 0 : v })
                  }}
                  error={modal.draftErrors.weight}
                />
                <FormText
                  id={`modal-height-${groupIndex}`}
                  name={`modal-height-${groupIndex}`}
                  label="Alto (cm) *"
                  value={modal.draft.height ? String(modal.draft.height) : ''}
                  inputMode="decimal"
                  onChange={(e: any) => {
                    const v = parseFloat(e.target.value.replace(',', '.'))
                    updateDraft({ height: isNaN(v) ? 0 : v })
                  }}
                  error={modal.draftErrors.height}
                />
                <FormText
                  id={`modal-width-${groupIndex}`}
                  name={`modal-width-${groupIndex}`}
                  label="Ancho (cm) *"
                  value={modal.draft.width ? String(modal.draft.width) : ''}
                  inputMode="decimal"
                  onChange={(e: any) => {
                    const v = parseFloat(e.target.value.replace(',', '.'))
                    updateDraft({ width: isNaN(v) ? 0 : v })
                  }}
                  error={modal.draftErrors.width}
                />
                <FormText
                  id={`modal-length-${groupIndex}`}
                  name={`modal-length-${groupIndex}`}
                  label="Largo (cm) *"
                  value={modal.draft.length ? String(modal.draft.length) : ''}
                  inputMode="decimal"
                  onChange={(e: any) => {
                    const v = parseFloat(e.target.value.replace(',', '.'))
                    updateDraft({ length: isNaN(v) ? 0 : v })
                  }}
                  error={modal.draftErrors.length}
                />
                <FormText
                  id={`modal-declaredValue-${groupIndex}`}
                  name={`modal-declaredValue-${groupIndex}`}
                  label="Valor declarado *"
                  value={modal.draft.declaredValue ? String(modal.draft.declaredValue) : ''}
                  inputMode="decimal"
                  onChange={(e: any) => {
                    const v = parseFloat(e.target.value.replace(',', '.'))
                    updateDraft({ declaredValue: isNaN(v) ? 0 : v })
                  }}
                  error={modal.draftErrors.declaredValue}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md border border-gray-200 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <PrimaryButton onClick={handleSave} disabled={isGuardarDisabled} className="text-sm">
                Guardar
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
